import * as React from "react";
import { redactTraceValue, traceSpanSchema, type TraceSpan } from "@zworks/console-contracts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Check,
  CheckCircle2,
  Copy,
  Cpu,
  Sparkles,
  Terminal,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import { formatDurationMs, formatMicroUSD, formatTokens } from "./trace-types";

interface SpanInspectorProps {
  span: TraceSpan | null;
  onClose: () => void;
}

export function SpanInspector({ span: rawSpan, onClose }: SpanInspectorProps) {
  const [copied, setCopied] = React.useState(false);
  const span = rawSpan ? traceSpanSchema.parse(redactTraceValue(rawSpan)) : null;

  if (!span) return null;

  const failed = span.status.toLowerCase() === "error" || Boolean(span.attributes.errorMessage);
  const llm = span.attributes.llm as
    | {
        model?: string;
        usage?: {
          inputTokens?: number;
          outputTokens?: number;
          totalTokens?: number;
          cacheReadTokens?: number;
          cacheWriteTokens?: number;
        };
        costMicroUSD?: number;
        response?: string;
        temperature?: number;
      }
    | undefined;

  const tool = span.attributes.tool as
    | {
        toolName?: string;
        mcpServer?: string;
        inputArgs?: unknown;
        output?: string;
        errorMsg?: string;
        exitCode?: number;
      }
    | undefined;

  const handleCopyRaw = () => {
    void navigator.clipboard.writeText(JSON.stringify(span, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const defaultTab = llm ? "llm" : tool ? "tool" : "overview";

  return (
    <aside
      aria-label="Span Inspector"
      className="flex h-full flex-col border-l border-border/70 bg-card text-foreground"
    >
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-border/70 p-3.5">
        <div className="flex min-w-0 items-center gap-2">
          {span.kind === "llm" || llm ? (
            <Sparkles className="size-4 shrink-0 text-lime" aria-hidden />
          ) : span.kind === "tool" || tool ? (
            <Wrench className="size-4 shrink-0 text-sky-400" aria-hidden />
          ) : (
            <Cpu className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          )}
          <div className="min-w-0">
            <h3 className="truncate font-mono text-xs font-semibold text-foreground">
              {span.name}
            </h3>
            <span className="font-mono text-xs text-muted-foreground">{span.spanId}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Badge variant={failed ? "danger" : "success"}>
            {failed ? (
              <XCircle className="size-3" aria-hidden />
            ) : (
              <CheckCircle2 className="size-3" aria-hidden />
            )}
            {failed ? "Failed" : span.status}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 rounded-md text-muted-foreground hover:text-foreground"
            onClick={onClose}
            aria-label="Close inspector"
          >
            <X className="size-4" aria-hidden />
          </Button>
        </div>
      </div>

      {/* Body with Tabs */}
      <div className="flex-1 overflow-y-auto p-3.5">
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="mb-3 grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value={llm ? "llm" : "tool"} disabled={!llm && !tool}>
              {llm ? "Model Call" : tool ? "Tool Exec" : "Details"}
            </TabsTrigger>
            <TabsTrigger value="raw">Raw JSON</TabsTrigger>
          </TabsList>

          {/* Tab 1: Overview */}
          <TabsContent value="overview" className="space-y-3.5">
            {failed && (
              <div
                role="alert"
                className="rounded-lg border border-danger/40 bg-danger/10 p-3 text-xs text-danger"
              >
                <p className="font-semibold">Execution Failure</p>
                <p className="mt-1 font-mono break-all">
                  {typeof span.attributes.errorMessage === "string"
                    ? span.attributes.errorMessage
                    : (tool?.errorMsg ?? "Span finished with error status.")}
                </p>
              </div>
            )}

            <div className="rounded-lg border border-border/60 bg-muted/20 p-3 text-xs space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Kind:</span>
                <span className="font-mono font-medium">{span.kind}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration:</span>
                <span className="font-mono font-medium">{formatDurationMs(span.durationMs)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Started:</span>
                <span className="font-mono font-medium text-xs">
                  {new Date(span.startedAt).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    fractionalSecondDigits: 3,
                  })}
                </span>
              </div>
              {span.parentSpanId && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Parent Span:</span>
                  <span className="font-mono text-xs">{span.parentSpanId}</span>
                </div>
              )}
            </div>

            {/* Metrics Chips */}
            {(llm || tool) && (
              <div className="grid grid-cols-2 gap-2">
                {llm && (
                  <>
                    <div className="rounded-lg border border-border/60 bg-card p-2.5 shadow-2xs">
                      <span className="text-xs text-muted-foreground">Tokens Spent</span>
                      <p className="mt-0.5 font-mono text-sm font-semibold">
                        {formatTokens(llm.usage?.totalTokens ?? 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatTokens(llm.usage?.inputTokens ?? 0)} in /{" "}
                        {formatTokens(llm.usage?.outputTokens ?? 0)} out
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-card p-2.5 shadow-2xs">
                      <span className="text-xs text-muted-foreground">Cost</span>
                      <p className="mt-0.5 font-mono text-sm font-semibold text-chart">
                        {formatMicroUSD(Number(llm.costMicroUSD ?? 0))}
                      </p>
                      <p className="text-xs text-muted-foreground">{llm.model ?? "unknown"}</p>
                    </div>
                  </>
                )}
                {tool && (
                  <div className="col-span-2 rounded-lg border border-border/60 bg-card p-2.5 shadow-2xs">
                    <span className="text-xs text-muted-foreground">Tool Target</span>
                    <p className="mt-0.5 font-mono text-sm font-semibold">
                      {tool.toolName ?? "custom_tool"}
                    </p>
                    {tool.exitCode !== undefined && (
                      <p className="text-xs text-muted-foreground">Exit code: {tool.exitCode}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Tab 2: LLM Call Details */}
          {llm && (
            <TabsContent value="llm" className="space-y-3">
              <div className="rounded-lg border border-border/60 bg-muted/20 p-2.5 text-xs space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Model:</span>
                  <span className="font-mono font-bold text-foreground">{llm.model}</span>
                </div>
                {llm.temperature !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Temperature:</span>
                    <span className="font-mono">{llm.temperature}</span>
                  </div>
                )}
                {llm.usage?.cacheReadTokens !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cache Read:</span>
                    <span className="font-mono text-chart">
                      {formatTokens(llm.usage.cacheReadTokens)}
                    </span>
                  </div>
                )}
              </div>

              {llm.response && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                    Model Response
                  </span>
                  <pre className="max-h-48 overflow-auto rounded-lg border border-chart/30 bg-chart/5 p-2.5 font-mono text-xs text-foreground">
                    {llm.response}
                  </pre>
                </div>
              )}
            </TabsContent>
          )}

          {/* Tab 2 (Alternative): Tool Call Details */}
          {tool && (
            <TabsContent value="tool" className="space-y-3">
              <div className="rounded-lg border border-border/60 bg-muted/20 p-2.5 text-xs space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tool Name:</span>
                  <span className="font-mono font-bold text-foreground">{tool.toolName}</span>
                </div>
                {tool.mcpServer && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">MCP Server:</span>
                    <span className="font-mono">{tool.mcpServer}</span>
                  </div>
                )}
                {tool.exitCode !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Exit Code:</span>
                    <span className="font-mono">{tool.exitCode}</span>
                  </div>
                )}
              </div>

              {tool.inputArgs !== undefined && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                    Arguments
                  </span>
                  <pre className="max-h-40 overflow-auto rounded-lg border border-border/60 bg-muted/40 p-2.5 font-mono text-xs">
                    {typeof tool.inputArgs === "string"
                      ? tool.inputArgs
                      : JSON.stringify(tool.inputArgs, null, 2)}
                  </pre>
                </div>
              )}

              {tool.output && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase flex items-center gap-1.5">
                    <Terminal className="size-3.5" aria-hidden /> Execution Output
                  </span>
                  <pre className="max-h-60 overflow-auto rounded-lg border border-border/70 bg-black/90 p-2.5 font-mono text-xs text-green-400">
                    {tool.output}
                  </pre>
                </div>
              )}
            </TabsContent>
          )}

          {/* Tab 3: Raw JSON / OTLP */}
          <TabsContent value="raw" className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">OTLP Span JSON</span>
              <Button
                variant="outline"
                size="sm"
                className="h-6 gap-1 px-2 text-xs"
                onClick={handleCopyRaw}
              >
                {copied ? (
                  <Check className="size-3 text-good" aria-hidden />
                ) : (
                  <Copy className="size-3" aria-hidden />
                )}
                {copied ? "Copied" : "Copy JSON"}
              </Button>
            </div>
            <pre className="max-h-96 overflow-auto rounded-lg border border-border/60 bg-muted/40 p-2.5 font-mono text-xs text-foreground">
              {JSON.stringify(span, null, 2)}
            </pre>
          </TabsContent>
        </Tabs>
      </div>
    </aside>
  );
}
